import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Drawer } from '@mantine/core';
import { AuthContext } from '../../../contexts/Auth/Auth.Context';
import { maskName } from '../../../utils/privacy';
import { splitSpecies } from '../../../utils/species';

/* ════════════════════════════════════════════════════════════════════
   TYPES
══════════════════════════════════════════════════════════════════════ */
interface GraphNode {
  id: string;
  label: string;
  nationality: string;
  caseCount: number;
  species: string[];
  locations: string[];
  cases: any[];
  aliases: string[];        // alternate spellings this same person was recorded under
  // physics
  x: number;
  y: number;
  vx: number;
  vy: number;
}

interface GraphEdge {
  source: string;
  target: string;
  weight: number;         // number of shared attributes
  type: 'species' | 'location' | 'both';
}

type LinkMode = 'species' | 'location' | 'both';

interface Props { allData: any[] }

/* ════════════════════════════════════════════════════════════════════
   COLOUR HELPERS
   Nationality color used to be assigned in render order, so the same
   nationality could silently change color when a filter changed the
   traversal order — and with 15 arbitrary hues, several pairs (e.g.
   #a78bfa/#60a5fa) are effectively identical under colorblind vision on
   this graph's dark background (validated: OKLab ΔE 0.3 under deutan).
   On a node-link graph any two colors can end up adjacent, so — per the
   data-viz palette method — only 3 hues can be made distinguishable to
   everyone in every pairing; beyond that no hue choice can guarantee
   separation. So: the 3 most common nationalities overall (stable across
   every filter) get these validated hues, everyone else shares one
   neutral tone, and a legend spells out the mapping.
══════════════════════════════════════════════════════════════════════ */
const NAT_SAFE_HUES  = ['#3987e5', '#d95926', '#199e70']; // blue / orange / aqua — validated all-pairs on this graph's dark surface
const NAT_OTHER_COLOR = '#8b93a1';                          // neutral — "everyone else", not a competing hue

/* ════════════════════════════════════════════════════════════════════
   DATA HELPERS
   caseStatus is free text (e.g. "Ongoing - Mn. 9/27/2022", "Concluded -
   based on the acquittal from the main Sheikh's Case") — only ~70% of
   real records equal "Ongoing"/"Concluded" exactly, so outcome counts
   must match on a PREFIX, not an exact string, or they silently miss
   a third of the data.
══════════════════════════════════════════════════════════════════════ */
function classifyStatus(raw?: string): 'ongoing' | 'concluded' | 'other' {
  const s = (raw || '').trim().toLowerCase();
  if (s.startsWith('ongoing')) return 'ongoing';
  if (s.startsWith('concluded')) return 'concluded';
  return 'other';
}

// dateOfSeizure mixes ISO ("2024-06-15") and "DD/MM/YYYY" ("14/09/2020")
// in real data — a plain string/`new Date()` comparison silently mis-sorts
// or misparses the second form, so both are parsed explicitly here.
function parseSeizureDate(raw?: string): number | null {
  if (!raw) return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (iso) return Date.UTC(+iso[1], +iso[2] - 1, +iso[3]);
  const dmy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw);
  if (dmy) return Date.UTC(+dmy[3], +dmy[2] - 1, +dmy[1]);
  return null;
}

// Splits "Jeremiah Kodi Alias Rasta" into ["Jeremiah Kodi", "Rasta"] so the
// same real person can be recognised whichever name a later case uses.
// Names without "alias" pass through unchanged as a single-element array.
function parseAliasNames(raw: string): string[] {
  const parts = raw.split(/\balias\b/i).map((p) => p.trim()).filter(Boolean);
  return parts.length > 1 ? parts : [raw.trim()];
}

/* ════════════════════════════════════════════════════════════════════
   PHYSICS CONSTANTS
══════════════════════════════════════════════════════════════════════ */
const REPULSION     = 6000;
const SPRING_K      = 0.04;
const SPRING_LEN    = 120;
const DAMPING       = 0.88;          // higher = settles faster
const CENTER_PULL   = 0.018;
const MAX_NODES     = 80;
const SETTLE_TICKS  = 280;           // run this many ticks then freeze
const ENERGY_CUTOFF = 0.08;          // also stop early if KE drops below this
const DRAG_SETTLE   = 120;           // ticks to re-run after a drag

/* ════════════════════════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════════════════════════ */
export default function NetworkAnalysisView({ allData }: Props) {
  const { state } = useContext(AuthContext);
  const role = state.userData?.role?.toLowerCase() ?? 'guest';
  const svgRef      = useRef<SVGSVGElement>(null);
  const simRef      = useRef<{ nodes: GraphNode[]; edges: GraphEdge[] } | null>(null);
  const rafRef      = useRef<number>(0);
  const dragRef     = useRef<{ id: string; ox: number; oy: number } | null>(null);
  const ticksRef    = useRef(0);       // ticks run in current burst
  const frozenRef   = useRef(false);   // true = simulation stopped
  const [, forceRender] = useState(0);
  const [frozen, setFrozen] = useState(false);

  const [linkMode,   setLinkMode]   = useState<LinkMode>('species');
  const [minWeight,  setMinWeight]  = useState(1);
  const [selected,   setSelected]   = useState<GraphNode | null>(null);
  const [search,     setSearch]     = useState('');
  const [simReady,   setSimReady]   = useState(false);
  const [helpOpen,   setHelpOpen]   = useState(false);

  /* ── Scope filters (narrow the dataset BEFORE the top-80 render cap,
        so any offender can be reached by filtering down to them) ──── */
  const [filterNationality, setFilterNationality] = useState('all');
  const [filterSpecies,     setFilterSpecies]     = useState('all');
  const [filterStatus,      setFilterStatus]      = useState<'all' | 'ongoing' | 'concluded' | 'other'>('all');
  const [dateFrom,          setDateFrom]          = useState('');
  const [dateTo,            setDateTo]            = useState('');

  /* ── Path finder ───────────────────────────────────────────────── */
  const [pathMode,  setPathMode]  = useState(false);
  const [pathStart, setPathStart] = useState<string | null>(null);
  const [pathEnd,   setPathEnd]   = useState<string | null>(null);

  /* ── Export menu ───────────────────────────────────────────────── */
  const [exportOpen, setExportOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  /* ── Hover (quick-glance tooltip, no click required) ──────────────── */
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  /* ── Public: re-heat the simulation (called by button or on new data) */
  const reheat = useCallback((ticks = SETTLE_TICKS) => {
    cancelAnimationFrame(rafRef.current);
    ticksRef.current = 0;
    frozenRef.current = false;
    setFrozen(false);
    setSimReady(false);
    // reset velocities so layout re-runs visibly
    simRef.current?.nodes.forEach((n) => { n.vx = (Math.random() - 0.5) * 2; n.vy = (Math.random() - 0.5) * 2; });
    startSim(ticks);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Filter option lists (always derived from the FULL dataset, so
        picking one filter never shrinks the choices for another) ──── */
  const nationalityOptions = useMemo(() => {
    const set = new Set<string>();
    allData.forEach((d: any) => { if (d.nationality?.trim()) set.add(d.nationality.trim()); });
    return Array.from(set).sort();
  }, [allData]);

  const speciesOptions = useMemo(() => {
    const set = new Set<string>();
    allData.forEach((d: any) => splitSpecies(d.species).forEach((s) => set.add(s.toLowerCase())));
    return Array.from(set).sort();
  }, [allData]);

  /* ── Nationality color: computed ONCE from the full unfiltered dataset,
        so a given nationality's color never changes across filter states
        (it either holds one of the 3 safe slots always, or is always
        "Other" — never reassigned based on what's currently in scope). ── */
  const dominantNationalities = useMemo(() => {
    const freq = new Map<string, number>();
    allData.forEach((d: any) => {
      const nat = (d.nationality || '').trim();
      if (nat) freq.set(nat, (freq.get(nat) || 0) + 1);
    });
    return Array.from(freq.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, NAT_SAFE_HUES.length)
      .map(([nat]) => nat);
  }, [allData]);

  const natColorMap = useMemo(() => {
    const m = new Map<string, string>();
    dominantNationalities.forEach((nat, i) => m.set(nat.toLowerCase(), NAT_SAFE_HUES[i]));
    return m;
  }, [dominantNationalities]);

  const natColor = useCallback(
    (nat: string) => natColorMap.get((nat || '').trim().toLowerCase()) ?? NAT_OTHER_COLOR,
    [natColorMap]
  );

  /* ── Apply scope filters before the graph is built ────────────────
        Records whose date can't be parsed are kept (not dropped) when a
        date range is set — hiding a real offender due to bad date data
        would be worse than occasionally including one outside the range. */
  const filteredData = useMemo(() => {
    const from = dateFrom ? parseSeizureDate(dateFrom) : null;
    const to   = dateTo   ? parseSeizureDate(dateTo)   : null;
    return allData.filter((d: any) => {
      if (filterNationality !== 'all' && (d.nationality || '').trim().toLowerCase() !== filterNationality.toLowerCase()) return false;
      if (filterSpecies !== 'all' && !splitSpecies(d.species).some((s) => s.toLowerCase() === filterSpecies)) return false;
      if (filterStatus !== 'all' && classifyStatus(d.caseStatus) !== filterStatus) return false;
      const dt = parseSeizureDate(d.dateOfSeizure);
      if (from != null && dt != null && dt < from) return false;
      if (to != null && dt != null && dt > to) return false;
      return true;
    });
  }, [allData, filterNationality, filterSpecies, filterStatus, dateFrom, dateTo]);

  /* ── Build graph data ─────────────────────────────────────────── */
  const { nodes: rawNodes, edges: rawEdges, totalOffenders, totalRepeat } = useMemo(() => {
    // Aggregate offenders. A record's name may itself be "X alias Y" — both
    // spellings are registered as keys pointing at the SAME node object, so
    // a different case that only uses one of the aliases still merges into
    // this person instead of fragmenting their network into two nodes.
    const offMap = new Map<string, GraphNode>();
    filteredData.forEach((d: any) => {
      const rawName = d.nameOfAccused?.trim();
      if (!rawName) return;
      const aliasNames = parseAliasNames(rawName);
      const aliasKeys  = aliasNames.map((a) => a.toLowerCase());

      // Aliases are masked the same as the primary label — an "Also known
      // as" list must respect the same role-based redaction, not leak a
      // real name past it.
      const maskedAliases = aliasNames.map((a) => maskName(a, role));
      let node = aliasKeys.map((k) => offMap.get(k)).find(Boolean) ?? null;
      if (!node) {
        node = {
          id: aliasKeys[0], label: maskedAliases[0],
          nationality: d.nationality || 'Unknown',
          caseCount: 0,
          species: [], locations: [], cases: [],
          aliases: maskedAliases.slice(1),
          x: 0, y: 0, vx: 0, vy: 0,
        };
      } else {
        maskedAliases.forEach((a) => { if (a !== node!.label && !node!.aliases.includes(a)) node!.aliases.push(a); });
      }
      aliasKeys.forEach((k) => offMap.set(k, node!));

      node.caseCount++;
      node.cases.push(d);
      splitSpecies(d.species).forEach((s) => {
        const sp = s.toLowerCase();
        if (!node!.species.includes(sp)) node!.species.push(sp);
      });
      const loc = d.location?.trim().toLowerCase();
      if (loc && !node.locations.includes(loc)) node.locations.push(loc);
    });

    // offMap can hold the SAME node object under several alias keys —
    // dedupe by node.id before counting or listing offenders.
    const uniqueNodes = new Map<string, GraphNode>();
    offMap.forEach((n) => { if (!uniqueNodes.has(n.id)) uniqueNodes.set(n.id, n); });

    // True totals across ALL offenders in the current scope, before the render cap below
    const totalOffenders = uniqueNodes.size;
    let totalRepeat = 0;
    uniqueNodes.forEach((n) => { if (n.caseCount > 1) totalRepeat++; });

    // Cap node count for the physics graph itself (take highest-case-count first) —
    // rendering 1000+ force-directed nodes is unreadable and slow, but the KPIs
    // above must still reflect the true totals, not just this visualised subset.
    const sorted = Array.from(uniqueNodes.values())
      .sort((a, b) => b.caseCount - a.caseCount)
      .slice(0, MAX_NODES);

    // Assign random initial positions in a circle
    const R = 280;
    sorted.forEach((n, i) => {
      const angle = (i / sorted.length) * 2 * Math.PI;
      n.x = 400 + R * Math.cos(angle) + (Math.random() - 0.5) * 40;
      n.y = 300 + R * Math.sin(angle) + (Math.random() - 0.5) * 40;
      n.vx = 0; n.vy = 0;
    });

    // Build edge set
    const edgeMap = new Map<string, GraphEdge>();
    const ids = sorted.map((n) => n.id);
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        const a = uniqueNodes.get(ids[i])!;
        const b = uniqueNodes.get(ids[j])!;

        const sharedSpecies   = a.species.filter((s) => b.species.includes(s)).length;
        const sharedLocations = a.locations.filter((l) => b.locations.includes(l)).length;

        if (sharedSpecies === 0 && sharedLocations === 0) continue;

        const type: GraphEdge['type'] =
          sharedSpecies > 0 && sharedLocations > 0 ? 'both' :
          sharedSpecies > 0 ? 'species' : 'location';

        const ekey = `${ids[i]}|${ids[j]}`;
        edgeMap.set(ekey, {
          source: ids[i], target: ids[j],
          weight: sharedSpecies + sharedLocations,
          type,
        });
      }
    }

    return { nodes: sorted, edges: Array.from(edgeMap.values()), totalOffenders, totalRepeat };
  }, [filteredData, role]);

  /* ── Filtered edges for current mode ─────────────────────────── */
  const visibleEdges = useMemo(() =>
    rawEdges.filter((e) =>
      (linkMode === 'both'     ? true : e.type === linkMode || e.type === 'both') &&
      e.weight >= minWeight
    ),
    [rawEdges, linkMode, minWeight]
  );

  /* ── Initialise simulation ref ────────────────────────────────── */
  useEffect(() => {
    simRef.current = {
      nodes: rawNodes.map((n) => ({ ...n })),
      edges: visibleEdges,
    };
    setSimReady(false);
    setSelected(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawNodes]);

  /* ── Update edges without resetting positions ─────────────────── */
  useEffect(() => {
    if (simRef.current) simRef.current.edges = visibleEdges;
  }, [visibleEdges]);

  /* ── Physics simulation (budgeted — stops itself) ─────────────── */
  const startSim = useCallback((budget: number) => {
    const SVG_W = 800, SVG_H = 580;

    function tick() {
      const sim = simRef.current;
      if (!sim || frozenRef.current) return;
      const { nodes, edges } = sim;

      // Repulsion
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[j].x - nodes[i].x;
          const dy = nodes[j].y - nodes[i].y;
          const dist2 = dx * dx + dy * dy + 0.01;
          const force = REPULSION / dist2;
          const nx = (dx / Math.sqrt(dist2)) * force;
          const ny = (dy / Math.sqrt(dist2)) * force;
          nodes[i].vx -= nx; nodes[i].vy -= ny;
          nodes[j].vx += nx; nodes[j].vy += ny;
        }
      }

      // Spring attraction
      const nodeIdx = new Map(nodes.map((n, i) => [n.id, i]));
      edges.forEach((e) => {
        const ai = nodeIdx.get(e.source), bi = nodeIdx.get(e.target);
        if (ai == null || bi == null) return;
        const a = nodes[ai], b = nodes[bi];
        const dx = b.x - a.x, dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy) + 0.01;
        const force = SPRING_K * (dist - SPRING_LEN) * Math.min(e.weight, 4);
        const nx = (dx / dist) * force, ny = (dy / dist) * force;
        a.vx += nx; a.vy += ny;
        b.vx -= nx; b.vy -= ny;
      });

      // Center gravity + integrate + dampen + clamp; measure kinetic energy
      let ke = 0;
      nodes.forEach((n) => {
        if (dragRef.current?.id === n.id) return;
        n.vx += (SVG_W / 2 - n.x) * CENTER_PULL;
        n.vy += (SVG_H / 2 - n.y) * CENTER_PULL;
        n.vx *= DAMPING; n.vy *= DAMPING;
        n.x  += n.vx;    n.y  += n.vy;
        n.x = Math.max(20, Math.min(SVG_W - 20, n.x));
        n.y = Math.max(20, Math.min(SVG_H - 20, n.y));
        ke += n.vx * n.vx + n.vy * n.vy;
      });

      ticksRef.current++;
      forceRender((v) => v + 1);

      // Mark ready at half-budget so layout is visible before freezing
      if (ticksRef.current === Math.floor(budget / 2)) setSimReady(true);

      // Stop when budget exhausted OR energy drops below cutoff
      if (ticksRef.current >= budget || (ticksRef.current > 40 && ke < ENERGY_CUTOFF * nodes.length)) {
        frozenRef.current = true;
        setFrozen(true);
        setSimReady(true);
        forceRender((v) => v + 1);
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Start simulation when graph data changes ──────────────────── */
  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    ticksRef.current  = 0;
    frozenRef.current = false;
    setFrozen(false);
    setSimReady(false);
    startSim(SETTLE_TICKS);
    return () => cancelAnimationFrame(rafRef.current);
  }, [rawNodes, startSim]);

  /* ── Drag handlers ────────────────────────────────────────────── */
  const onNodeMouseDown = useCallback((e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const svg = svgRef.current!;
    const pt  = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    dragRef.current = { id, ox: svgP.x, oy: svgP.y };
  }, []);

  useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!dragRef.current || !svgRef.current) return;
      const svg = svgRef.current;
      const pt  = svg.createSVGPoint();
      pt.x = e.clientX; pt.y = e.clientY;
      const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
      const node = simRef.current?.nodes.find((n) => n.id === dragRef.current?.id);
      if (node) { node.x = svgP.x; node.y = svgP.y; node.vx = 0; node.vy = 0; }
    }
    function onUp() {
      if (!dragRef.current) return;
      dragRef.current = null;
      // brief re-settle after drag so neighbours reposition cleanly
      cancelAnimationFrame(rafRef.current);
      ticksRef.current  = 0;
      frozenRef.current = false;
      setFrozen(false);
      startSim(DRAG_SETTLE);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [startSim]);

  /* ── Derived display data ─────────────────────────────────────── */
  const nodes = simRef.current?.nodes ?? [];
  const edges = simRef.current?.edges ?? [];

  const searchLower = search.toLowerCase();
  const filteredNodeIds = useMemo(() =>
    searchLower
      ? new Set(nodes.filter((n) => n.label.toLowerCase().includes(searchLower) || n.nationality.toLowerCase().includes(searchLower)).map((n) => n.id))
      : null,
    [nodes, searchLower]
  );

  const connectedToSelected = useMemo(() => {
    if (!selected) return null;
    const ids = new Set<string>();
    edges.forEach((e) => {
      if (e.source === selected.id) ids.add(e.target);
      if (e.target === selected.id) ids.add(e.source);
    });
    return ids;
  }, [selected, edges]);

  /* ── Path finder: shortest hop-chain between two offenders, using the
        currently-visible edges (i.e. respecting Link Mode / Min Links) ── */
  const foundPath = useMemo(() => {
    if (!pathStart || !pathEnd || pathStart === pathEnd) return null;
    const adj = new Map<string, string[]>();
    edges.forEach((e) => {
      if (!adj.has(e.source)) adj.set(e.source, []);
      if (!adj.has(e.target)) adj.set(e.target, []);
      adj.get(e.source)!.push(e.target);
      adj.get(e.target)!.push(e.source);
    });
    const queue: string[] = [pathStart];
    const prev = new Map<string, string>();
    const visited = new Set<string>([pathStart]);
    while (queue.length) {
      const cur = queue.shift()!;
      if (cur === pathEnd) break;
      (adj.get(cur) || []).forEach((next) => {
        if (!visited.has(next)) {
          visited.add(next);
          prev.set(next, cur);
          queue.push(next);
        }
      });
    }
    if (!visited.has(pathEnd)) return null;
    const path: string[] = [pathEnd];
    while (path[path.length - 1] !== pathStart) {
      const p = prev.get(path[path.length - 1]);
      if (!p) return null;
      path.push(p);
    }
    return path.reverse();
  }, [pathStart, pathEnd, edges]);

  /* ── Case-outcome breakdown for the selected offender ─────────────── */
  const outcomeCounts = useMemo(() => {
    if (!selected) return null;
    let ongoing = 0, concluded = 0, other = 0;
    selected.cases.forEach((c: any) => {
      const cls = classifyStatus(c.caseStatus);
      if (cls === 'ongoing') ongoing++;
      else if (cls === 'concluded') concluded++;
      else other++;
    });
    return { ongoing, concluded, other };
  }, [selected]);

  const pathNodeSet = useMemo(() => (foundPath ? new Set(foundPath) : null), [foundPath]);
  const pathEdgeSet = useMemo(() => {
    if (!foundPath) return null;
    const s = new Set<string>();
    for (let i = 0; i < foundPath.length - 1; i++) {
      s.add(`${foundPath[i]}|${foundPath[i + 1]}`);
      s.add(`${foundPath[i + 1]}|${foundPath[i]}`);
    }
    return s;
  }, [foundPath]);

  const edgeColor = (type: GraphEdge['type']) =>
    type === 'species'  ? 'rgba(74,222,128,0.35)' :
    type === 'location' ? 'rgba(96,165,250,0.35)' :
                          'rgba(249,115,22,0.5)';

  const edgeColorHighlight = (type: GraphEdge['type']) =>
    type === 'species'  ? '#4ade80' :
    type === 'location' ? '#60a5fa' :
                          '#f97316';

  const nodeRadius = (n: GraphNode) => Math.min(5 + n.caseCount * 2.5, 22);

  const isHighlighted = (id: string) =>
    pathNodeSet ? pathNodeSet.has(id) :
    !selected ? true :
    id === selected.id || connectedToSelected?.has(id);

  /* ── Close the export menu on outside click ───────────────────────── */
  useEffect(() => {
    if (!exportOpen) return;
    function onDocClick(e: MouseEvent) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) setExportOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [exportOpen]);

  /* ── Export: offender/link data as CSV ────────────────────────────── */
  const exportCSV = useCallback(() => {
    const esc = (v: unknown) => `"${String(v).replace(/"/g, '""')}"`;
    const rows: string[][] = [['Offender', 'Nationality', 'Cases', 'Species', 'Locations', 'Aliases']];
    rawNodes.forEach((n) => {
      rows.push([n.label, n.nationality, String(n.caseCount), n.species.join('; '), n.locations.join('; '), n.aliases.join('; ')]);
    });
    rows.push([]);
    rows.push(['Source', 'Target', 'Weight', 'Type']);
    rawEdges.forEach((e) => {
      const a = rawNodes.find((n) => n.id === e.source)?.label ?? e.source;
      const b = rawNodes.find((n) => n.id === e.target)?.label ?? e.target;
      rows.push([a, b, String(e.weight), e.type]);
    });
    const csv  = rows.map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `network-analysis-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [rawNodes, rawEdges]);

  /* ── Export: current graph layout as a PNG snapshot ───────────────── */
  const exportPNG = useCallback(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const clone = svgEl.cloneNode(true) as SVGSVGElement;
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', '800');
    bg.setAttribute('height', '580');
    bg.setAttribute('fill', '#061a0e');
    clone.insertBefore(bg, clone.firstChild);
    const svgString = new XMLSerializer().serializeToString(clone);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl  = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1600; canvas.height = 1160;
      const ctx = canvas.getContext('2d');
      URL.revokeObjectURL(svgUrl);
      if (!ctx) return;
      ctx.fillStyle = '#061a0e';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const pngUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = pngUrl;
        a.download = `network-graph-${new Date().toISOString().slice(0, 10)}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(pngUrl);
      }, 'image/png');
    };
    img.src = svgUrl;
  }, []);

  /* ── Stats ────────────────────────────────────────────────────── */
  const edgeCount     = visibleEdges.length;
  const clusterOf = useMemo(() => {
    // Connected-components — also used to draw a halo behind each tight cluster
    const parent = new Map(rawNodes.map((n) => [n.id, n.id]));
    function find(x: string): string { return parent.get(x) === x ? x : (parent.set(x, find(parent.get(x)!)), parent.get(x)!); }
    visibleEdges.forEach((e) => {
      const ra = find(e.source), rb = find(e.target);
      if (ra !== rb) parent.set(ra, rb);
    });
    const result = new Map<string, string>();
    rawNodes.forEach((n) => result.set(n.id, find(n.id)));
    return result;
  }, [rawNodes, visibleEdges]);
  const clusterCount = useMemo(() => new Set(clusterOf.values()).size, [clusterOf]);

  // Halo behind each cluster of 3+ offenders — a direct visual read for
  // "probable criminal cell", independent of nationality color. Computed
  // fresh every render (like the edge/node positions below) rather than
  // memoized: node.x/.y mutate in place every physics tick without the
  // `nodes` array reference itself changing, so a useMemo keyed on it
  // would freeze the halo mid-animation instead of tracking the layout.
  function computeClusterHalos() {
    const groups = new Map<string, GraphNode[]>();
    nodes.forEach((n) => {
      const root = clusterOf.get(n.id);
      if (!root) return;
      if (!groups.has(root)) groups.set(root, []);
      groups.get(root)!.push(n);
    });
    return Array.from(groups.values())
      .filter((g) => g.length >= 3)
      .map((g) => {
        const cx = g.reduce((s, n) => s + n.x, 0) / g.length;
        const cy = g.reduce((s, n) => s + n.y, 0) / g.length;
        const r  = Math.max(...g.map((n) => Math.hypot(n.x - cx, n.y - cy))) + 34;
        return { cx, cy, r, key: g.map((n) => n.id).sort().join('|') };
      });
  }
  const clusterHalos = computeClusterHalos();

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ background: '#061a0e' }}>

      {/* ── Header bar ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-white/6 shrink-0 flex-wrap" style={{ background: '#041f0e' }}>
        <div>
          <h2 className="text-white/85 text-sm font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Network Analysis</h2>
          <p className="text-white/25 text-[10px]">
            Force-directed offender relationship graph
            {totalOffenders > MAX_NODES && (
              <> &bull; graph shows top {MAX_NODES} of {totalOffenders} offenders in scope by case count</>
            )}
          </p>
        </div>

        {/* KPIs */}
        <div className="flex items-center gap-3 flex-wrap">
          {[
            { label: 'Offenders',  value: totalOffenders,   color: 'text-white/70' },
            { label: 'Repeat',     value: totalRepeat,      color: 'text-red-400' },
            { label: 'Links',      value: edgeCount,        color: 'text-amber-400' },
            { label: 'Clusters',   value: clusterCount,     color: 'text-sky-400' },
          ].map((s) => (
            <div key={s.label} className="flex flex-col items-center px-3 py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <span className={`text-lg font-bold tabular-nums leading-tight ${s.color}`} style={{ fontFamily: 'var(--font-display)' }}>{s.value}</span>
              <span className="text-white/25 text-[9px] uppercase tracking-wide">{s.label}</span>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Link mode */}
          <div className="flex items-center rounded-lg overflow-hidden border border-white/10 text-[10px] font-semibold uppercase tracking-wide" style={{ background: 'rgba(0,0,0,0.3)' }}>
            {(['species', 'location', 'both'] as LinkMode[]).map((m) => (
              <button
                key={m}
                onClick={() => setLinkMode(m)}
                className={`px-3 py-1.5 transition-all ${linkMode === m ? 'bg-green-700/60 text-green-300' : 'text-white/30 hover:text-white/55'}`}
              >
                {m === 'species' ? '🐘 Species' : m === 'location' ? '📍 Location' : '⚡ Both'}
              </button>
            ))}
          </div>

          {/* Min weight */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 text-[10px]" style={{ background: 'rgba(0,0,0,0.3)' }}>
            <span className="text-white/30 whitespace-nowrap">Min links:</span>
            <input
              type="range" min={1} max={5} value={minWeight}
              onChange={(e) => setMinWeight(Number(e.target.value))}
              className="w-16 accent-green-500"
            />
            <span className="text-white/55 tabular-nums w-3">{minWeight}</span>
          </div>

          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/25" />
            <input
              className="pl-7 pr-3 py-1.5 rounded-lg text-[10px] bg-white/5 border border-white/10 text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-green-500/40 w-36"
              placeholder="Search offender..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Freeze / Re-layout */}
          <button
            onClick={() => reheat(SETTLE_TICKS)}
            title="Re-run the layout"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-semibold transition-all bg-white/5 border-white/10 text-white/40 hover:text-white/70 hover:bg-white/8"
          >
            ↺ Re-layout
          </button>

          {/* Trace path */}
          <button
            onClick={() => {
              setPathMode((v) => !v);
              setPathStart(null);
              setPathEnd(null);
              setSelected(null);
            }}
            title="Trace the shortest connection path between two offenders"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-semibold transition-all ${
              pathMode
                ? 'bg-amber-500/20 border-amber-500/40 text-amber-300'
                : 'bg-white/5 border-white/10 text-white/40 hover:text-white/70 hover:bg-white/8'
            }`}
          >
            🔗 Trace Path
          </button>

          {/* Export */}
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setExportOpen((v) => !v)}
              title="Export the network graph or data"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-semibold transition-all bg-white/5 border-white/10 text-white/40 hover:text-white/70 hover:bg-white/8"
            >
              ⬇ Export
            </button>
            {exportOpen && (
              <div
                className="absolute right-0 top-full mt-1 z-30 flex flex-col rounded-lg overflow-hidden border border-white/10 text-[10px]"
                style={{ background: '#0a2e18', minWidth: 170 }}
              >
                <button
                  onClick={() => { exportPNG(); setExportOpen(false); }}
                  className="px-3 py-2.5 text-left text-white/60 hover:bg-white/8 hover:text-white/90 transition-colors"
                >
                  🖼️ Graph as PNG
                </button>
                <button
                  onClick={() => { exportCSV(); setExportOpen(false); }}
                  className="px-3 py-2.5 text-left text-white/60 hover:bg-white/8 hover:text-white/90 transition-colors border-t border-white/5"
                >
                  📄 Offenders &amp; links as CSV
                </button>
              </div>
            )}
          </div>

          {/* Help */}
          <button
            onClick={() => setHelpOpen(true)}
            title="How network analysis works"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-semibold transition-all bg-sky-500/10 border-sky-500/25 text-sky-400 hover:bg-sky-500/20"
          >
            <InformationCircleIcon className="w-3.5 h-3.5" />
            How it works
          </button>

          {/* Stable indicator */}
          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] border transition-all ${
            frozen
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
              : 'bg-amber-500/10 border-amber-500/20 text-amber-400'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${frozen ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'}`} />
            {frozen ? 'Stable' : 'Settling…'}
          </div>
        </div>
      </div>

      {/* ── Scope filters ──────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-5 py-2 border-b border-white/6 shrink-0 flex-wrap" style={{ background: '#03170b' }}>
        <span className="text-white/25 text-[9px] uppercase tracking-wider font-semibold">Scope:</span>

        <select
          value={filterNationality}
          onChange={(e) => setFilterNationality(e.target.value)}
          className="px-2.5 py-1.5 rounded-lg text-[10px] bg-white/5 border border-white/10 text-white/70 focus:outline-none focus:ring-1 focus:ring-green-500/40"
        >
          <option value="all">All nationalities</option>
          {nationalityOptions.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>

        <select
          value={filterSpecies}
          onChange={(e) => setFilterSpecies(e.target.value)}
          className="px-2.5 py-1.5 rounded-lg text-[10px] bg-white/5 border border-white/10 text-white/70 capitalize focus:outline-none focus:ring-1 focus:ring-green-500/40"
        >
          <option value="all">All species</option>
          {speciesOptions.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as 'all' | 'ongoing' | 'concluded' | 'other')}
          className="px-2.5 py-1.5 rounded-lg text-[10px] bg-white/5 border border-white/10 text-white/70 focus:outline-none focus:ring-1 focus:ring-green-500/40"
        >
          <option value="all">All statuses</option>
          <option value="ongoing">Ongoing</option>
          <option value="concluded">Concluded</option>
          <option value="other">Other / unclear</option>
        </select>

        <div className="flex items-center gap-1.5">
          <input
            type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            style={{ colorScheme: 'dark' }}
            className="px-2.5 py-1.5 rounded-lg text-[10px] bg-white/5 border border-white/10 text-white/70 focus:outline-none focus:ring-1 focus:ring-green-500/40"
          />
          <span className="text-white/20 text-[10px]">to</span>
          <input
            type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            style={{ colorScheme: 'dark' }}
            className="px-2.5 py-1.5 rounded-lg text-[10px] bg-white/5 border border-white/10 text-white/70 focus:outline-none focus:ring-1 focus:ring-green-500/40"
          />
        </div>

        {(filterNationality !== 'all' || filterSpecies !== 'all' || filterStatus !== 'all' || dateFrom || dateTo) && (
          <button
            onClick={() => { setFilterNationality('all'); setFilterSpecies('all'); setFilterStatus('all'); setDateFrom(''); setDateTo(''); }}
            className="text-[10px] text-white/35 hover:text-white/65 underline"
          >
            Reset filters
          </button>
        )}

        <span className="ml-auto text-white/20 text-[9px]">{filteredData.length} of {allData.length} cases in scope</span>
      </div>

      {/* ── Main area: graph + detail panel ────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* SVG graph */}
        <div className="flex-1 relative overflow-hidden">
          {!simReady && nodes.length > 0 && (
            <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none" style={{ background: 'rgba(6,26,14,0.6)', backdropFilter: 'blur(4px)' }}>
              <div className="flex flex-col items-center gap-3">
                <div className="w-6 h-6 rounded-full border-2 border-green-500/30 border-t-green-400 animate-spin" />
                <p className="text-white/50 text-xs">Calculating layout&hellip;</p>
              </div>
            </div>
          )}

          {pathMode && (
            <div
              className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3 px-4 py-2 rounded-xl text-[11px]"
              style={{ background: 'rgba(4,31,14,0.92)', border: '1px solid rgba(251,191,36,0.3)' }}
            >
              <span className="text-amber-300 font-semibold">🔗 Path finder</span>
              <span className="text-white/50">
                {!pathStart
                  ? 'Click the first offender'
                  : !pathEnd
                  ? 'Click the second offender'
                  : foundPath
                  ? `Path found — ${foundPath.length} offenders, ${foundPath.length - 1} links`
                  : 'No path between them under the current Link Mode / Min Links'}
              </span>
              {(pathStart || pathEnd) && (
                <button onClick={() => { setPathStart(null); setPathEnd(null); }} className="text-white/30 hover:text-white/60">
                  Clear
                </button>
              )}
            </div>
          )}

          {nodes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <p className="text-white/25 text-sm">
                {allData.length === 0 ? 'No offender data to visualise' : 'No offenders match the current scope filters'}
              </p>
              <p className="text-white/15 text-xs">
                {allData.length === 0
                  ? 'Upload seizure-case records with accused names to build the network'
                  : 'Try widening the nationality, species, status or date filters above'}
              </p>
            </div>
          ) : (
            <svg
              ref={svgRef}
              viewBox="0 0 800 580"
              className="w-full h-full"
              style={{ cursor: dragRef.current ? 'grabbing' : 'default', opacity: simReady ? 1 : 0.35, transition: 'opacity 0.5s ease' }}
              onClick={() => setSelected(null)}
            >
              <style>{`
                @keyframes witsPulseEdge { to { stroke-dashoffset: -20; } }
                .wits-pulse-edge { stroke-dasharray: 6 4; animation: witsPulseEdge 1s linear infinite; }
              `}</style>

              {/* Background grid */}
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
                </pattern>
                {/* Soft halo behind selected / path / high-value nodes */}
                <filter id="nodeGlow" x="-60%" y="-60%" width="220%" height="220%">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                {/* Glossy highlight overlay so nodes read as spheres, not flat dots */}
                <radialGradient id="nodeGloss" cx="35%" cy="28%" r="75%">
                  <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
                  <stop offset="45%" stopColor="rgba(255,255,255,0.08)" />
                  <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                </radialGradient>
              </defs>
              <rect width="800" height="580" fill="url(#grid)" />

              {/* Cluster halos — a direct visual read for "probable criminal cell" */}
              <g>
                {clusterHalos.map((h) => (
                  <circle
                    key={h.key} cx={h.cx} cy={h.cy} r={h.r}
                    fill="rgba(74,222,128,0.05)" stroke="rgba(74,222,128,0.15)" strokeWidth="1"
                  />
                ))}
              </g>

              {/* Edges */}
              <g>
                {edges.map((e, i) => {
                  const a = nodes.find((n) => n.id === e.source);
                  const b = nodes.find((n) => n.id === e.target);
                  if (!a || !b) return null;
                  const onPath = pathEdgeSet?.has(`${e.source}|${e.target}`);
                  const isActive = onPath || (selected
                    ? (e.source === selected.id || e.target === selected.id)
                    : (!filteredNodeIds || (filteredNodeIds.has(e.source) || filteredNodeIds.has(e.target))));
                  return (
                    <line
                      key={i}
                      className={onPath ? 'wits-pulse-edge' : undefined}
                      x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                      stroke={onPath ? '#facc15' : isActive ? edgeColorHighlight(e.type) : edgeColor(e.type)}
                      strokeWidth={onPath ? 3.5 : isActive ? Math.min(e.weight * 0.8, 3) : 0.8}
                      opacity={onPath ? 1 : isActive ? 0.75 : pathNodeSet ? 0.08 : 0.2}
                    />
                  );
                })}
              </g>

              {/* Nodes */}
              <g>
                {nodes.map((n) => {
                  const r  = nodeRadius(n);
                  const onPath = pathNodeSet?.has(n.id);
                  const hi = isHighlighted(n.id);
                  const dim = pathNodeSet ? !onPath : (filteredNodeIds && !filteredNodeIds.has(n.id));
                  const isSel = selected?.id === n.id;
                  const isHovered = hoveredId === n.id;
                  const col = natColor(n.nationality);
                  const glow = isSel || onPath || isHovered || n.caseCount > 2;
                  return (
                    <g
                      key={n.id}
                      transform={`translate(${n.x},${n.y})`}
                      style={{ cursor: 'pointer', transition: 'opacity 0.2s ease' }}
                      opacity={dim ? 0.12 : hi ? 1 : 0.25}
                      filter={glow ? 'url(#nodeGlow)' : undefined}
                      onMouseDown={(e) => { e.stopPropagation(); onNodeMouseDown(e, n.id); }}
                      onMouseEnter={() => setHoveredId(n.id)}
                      onMouseLeave={() => setHoveredId((h) => (h === n.id ? null : h))}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (pathMode) {
                          if (!pathStart || (pathStart && pathEnd)) { setPathStart(n.id); setPathEnd(null); }
                          else if (n.id !== pathStart) { setPathEnd(n.id); }
                          return;
                        }
                        setSelected(isSel ? null : n);
                      }}
                    >
                      {/* Glow ring for selected */}
                      {isSel && (
                        <circle r={r + 7} fill="none" stroke={col} strokeWidth="1.5" opacity="0.4" />
                      )}
                      {/* Path-finder highlight ring */}
                      {onPath && (
                        <circle r={r + 7} fill="none" stroke="#facc15" strokeWidth="2" opacity="0.7" />
                      )}
                      {/* Path start/end marker */}
                      {(n.id === pathStart || n.id === pathEnd) && (
                        <circle r={r + 3} fill="none" stroke="#facc15" strokeWidth="1.5" strokeDasharray="2 2" />
                      )}
                      {/* Repeat-offender ring */}
                      {n.caseCount > 1 && (
                        <circle r={r + 3} fill="none" stroke="rgba(239,68,68,0.6)" strokeWidth="1.5" strokeDasharray="3 2" />
                      )}
                      <circle r={r} fill={col} fillOpacity={isSel || isHovered ? 1 : 0.8} stroke="rgba(255,255,255,0.2)" strokeWidth="0.8" />
                      {/* Glossy highlight so nodes read as spheres, not flat dots */}
                      <circle r={r} fill="url(#nodeGloss)" style={{ pointerEvents: 'none' }} />
                      {/* Label */}
                      {(r >= 10 || isSel || onPath) && (
                        <text
                          y={r + 11}
                          textAnchor="middle"
                          fontSize={isSel ? 9 : 8}
                          fill={isSel ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)'}
                          style={{ pointerEvents: 'none', userSelect: 'none' }}
                        >
                          {n.label.split(' ')[0]}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>

              {/* Hover tooltip — quick glance without needing to click */}
              {hoveredId && !dragRef.current && (() => {
                const hn = nodes.find((n) => n.id === hoveredId);
                if (!hn) return null;
                const text = `${hn.label} · ${hn.nationality} · ${hn.caseCount} case${hn.caseCount === 1 ? '' : 's'}`;
                const w = Math.min(230, 16 + text.length * 4.6);
                const tx = Math.min(800 - w - 6, Math.max(6, hn.x - w / 2));
                const ty = Math.max(6, hn.y - nodeRadius(hn) - 26);
                return (
                  <g style={{ pointerEvents: 'none' }}>
                    <rect x={tx} y={ty} width={w} height={20} rx={5} fill="rgba(4,31,14,0.96)" stroke="rgba(255,255,255,0.14)" />
                    <text x={tx + w / 2} y={ty + 14} textAnchor="middle" fontSize={9.5} fill="rgba(255,255,255,0.85)">{text}</text>
                  </g>
                );
              })()}
            </svg>
          )}

          {/* Legend */}
          <div className="absolute bottom-3 left-3 flex flex-col gap-1.5 pointer-events-none">
            <div className="flex items-center gap-4 px-3 py-2 rounded-xl text-[9px] flex-wrap max-w-md" style={{ background: 'rgba(4,31,14,0.88)', border: '1px solid rgba(255,255,255,0.07)' }}>
              {[
                { color: '#4ade80', label: 'Shared species' },
                { color: '#60a5fa', label: 'Shared location' },
                { color: '#f97316', label: 'Both' },
              ].map((l) => (
                <span key={l.label} className="flex items-center gap-1.5 text-white/40">
                  <span className="w-6 h-0.5 inline-block rounded" style={{ background: l.color }} />
                  {l.label}
                </span>
              ))}
              <span className="flex items-center gap-1.5 text-white/40">
                <span className="w-3 h-3 rounded-full inline-block border border-red-500/60 border-dashed" />
                Repeat offender
              </span>
              <span className="w-px h-3 bg-white/10" />
              {dominantNationalities.map((nat, i) => (
                <span key={nat} className="flex items-center gap-1.5 text-white/40">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: NAT_SAFE_HUES[i] }} />
                  {nat}
                </span>
              ))}
              <span className="flex items-center gap-1.5 text-white/40">
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: NAT_OTHER_COLOR }} />
                Other nationality
              </span>
            </div>
            <p className="text-white/20 text-[9px] px-1">Drag nodes &bull; Click to inspect &bull; Hover for a quick glance &bull; Nodes sized by case count</p>
          </div>
        </div>

        {/* ── Detail panel ───────────────────────────────────────── */}
        {selected && (
          <div
            className="flex flex-col gap-4 overflow-y-auto shrink-0 border-l border-white/6 p-4"
            style={{ width: 260, background: '#041f0e', scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[9px] uppercase tracking-widest text-white/25 mb-1">Offender Profile</p>
                <p className="text-white/85 text-sm font-bold leading-tight">{selected.label}</p>
                <p className="text-white/35 text-[10px] mt-0.5">{selected.nationality}</p>
                {selected.aliases.length > 0 && (
                  <p className="text-amber-400/70 text-[9px] mt-1">Also known as: {selected.aliases.join(', ')}</p>
                )}
              </div>
              <button onClick={() => setSelected(null)} className="text-white/25 hover:text-white/55 transition-colors p-1">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Cases',       value: selected.caseCount,                    color: 'text-white/75' },
                { label: 'Connections', value: connectedToSelected?.size ?? 0,        color: 'text-amber-400' },
                { label: 'Species',     value: selected.species.length,               color: 'text-green-400' },
                { label: 'Locations',   value: selected.locations.length,             color: 'text-sky-400' },
              ].map((s) => (
                <div key={s.label} className="rounded-lg px-3 py-2" style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <p className={`text-xl font-bold tabular-nums ${s.color}`} style={{ fontFamily: 'var(--font-display)' }}>{s.value}</p>
                  <p className="text-white/25 text-[9px]">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Case outcomes */}
            {outcomeCounts && (
              <div>
                <p className="text-white/30 text-[9px] uppercase tracking-wider mb-2">Case Outcomes</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-400 text-[9px]">
                    {outcomeCounts.ongoing} Ongoing
                  </span>
                  <span className="px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[9px]">
                    {outcomeCounts.concluded} Concluded
                  </span>
                  {outcomeCounts.other > 0 && (
                    <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/40 text-[9px]">
                      {outcomeCounts.other} Other
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Species */}
            {selected.species.length > 0 && (
              <div>
                <p className="text-white/30 text-[9px] uppercase tracking-wider mb-2">Species Involved</p>
                <div className="flex flex-wrap gap-1">
                  {selected.species.map((s) => (
                    <span key={s} className="px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-[9px] capitalize">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Connected offenders */}
            {connectedToSelected && connectedToSelected.size > 0 && (
              <div>
                <p className="text-white/30 text-[9px] uppercase tracking-wider mb-2">Connected Offenders ({connectedToSelected.size})</p>
                <div className="flex flex-col gap-1">
                  {Array.from(connectedToSelected).slice(0, 12).map((id) => {
                    const peer = nodes.find((n) => n.id === id);
                    const edge = edges.find((e) =>
                      (e.source === selected.id && e.target === id) ||
                      (e.target === selected.id && e.source === id)
                    );
                    if (!peer) return null;
                    return (
                      <button
                        key={id}
                        onClick={(ev) => { ev.stopPropagation(); setSelected(peer); }}
                        className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg hover:bg-white/5 transition-all text-left"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-4 h-4 rounded-full shrink-0" style={{ background: natColor(peer.nationality) }} />
                          <span className="text-white/60 text-[10px] truncate">{peer.label}</span>
                        </div>
                        <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${
                          edge?.type === 'species'  ? 'bg-green-500/15 text-green-400' :
                          edge?.type === 'location' ? 'bg-sky-500/15 text-sky-400' :
                          'bg-orange-500/15 text-orange-400'
                        }`}>
                          {edge?.weight}× {edge?.type}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Case list */}
            <div>
              <p className="text-white/30 text-[9px] uppercase tracking-wider mb-2">Case Records</p>
              <div className="flex flex-col gap-1">
                {selected.cases.slice(0, 8).map((c: any, i: number) => (
                  <div key={i} className="px-2.5 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <p className="text-white/55 text-[10px] font-medium capitalize truncate">{c.species || '—'}</p>
                    <p className="text-white/25 text-[9px] truncate">{c.location || '—'} · {c.dateOfSeizure || '—'}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════════
          HELP DRAWER
      ════════════════════════════════════════════════════════════ */}
      <Drawer
        opened={helpOpen}
        onClose={() => setHelpOpen(false)}
        position="right"
        title="How Network Analysis Works"
        size="lg"
        zIndex={1200}
        styles={{
          drawer:      { background: '#041f0e', borderLeft: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column' },
          header:      { background: '#041f0e', borderBottom: '1px solid rgba(255,255,255,0.07)', paddingTop: 16, paddingBottom: 16 },
          title:       { color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: 14, letterSpacing: '0.02em' },
          closeButton: { color: 'rgba(255,255,255,0.35)' },
          body:        { flex: 1, overflowY: 'auto', padding: '20px 24px', scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' },
        }}
      >
        <div className="flex flex-col gap-6 text-white/70 text-sm leading-relaxed">

          {/* Intro */}
          <div className="rounded-xl border border-sky-500/20 px-4 py-3" style={{ background: 'rgba(14,165,233,0.07)' }}>
            <p className="text-sky-300 text-xs font-semibold uppercase tracking-wider mb-1">Overview</p>
            <p className="text-white/60 text-xs leading-relaxed">
              This finds connections between offenders that you&apos;d never spot scrolling through case records — who knows
              whom, how strong that link looks, and where a network might be forming.
            </p>
          </div>

          {/* Nodes & Edges */}
          <Section title="Nodes and edges">
            <p>
              Each circle is one accused person. Bigger circle, more cases against that name — that&apos;s the whole rule.
            </p>
            <p className="mt-2">
              A line between two people means they have something in common: same species, same location, or both.
              No line just means nothing in common yet, not that they&apos;re unrelated.
            </p>
            <p className="mt-2">
              Color marks nationality, but only for the <strong className="text-white/85">3 most common</strong> ones —
              they&apos;re in the legend. Past three colors most people, colorblind viewers especially, can&apos;t reliably tell
              them apart on screen, so we didn&apos;t pretend otherwise. Everyone else is grey; hover or click a node to see
              their actual nationality.
            </p>
            <p className="mt-2">
              The soft green glow around a group of three or more nodes is a better signal anyway — that&apos;s the fastest
              way to spot a likely cell, whatever nationalities are involved.
            </p>
          </Section>

          {/* Physics layout */}
          <Section title="Why nodes drift into clusters">
            <p>
              It&apos;s a simple physics trick: connected people pull toward each other, everyone else pushes apart. Run it
              long enough and it settles on its own — nobody has to arrange the nodes by hand.
            </p>
            <p className="mt-2">
              So when a handful of offenders end up sitting close together, that&apos;s not the layout being messy. It
              usually means there&apos;s a real cluster there.
            </p>
          </Section>

          {/* What to look for */}
          <Section title="What to look for">
            <div className="flex flex-col gap-2 mt-1">
              {[
                { icon: '🔵', term: 'Tight cluster',         desc: 'A few offenders sitting close together, sharing species or location. Worth investigating as a group.' },
                { icon: '⭕', term: 'Large central node',     desc: 'Lots of connections. Usually a coordinator or a repeat player, not a one-off.' },
                { icon: '🔴', term: 'Dashed red ring',        desc: "This person shows up in more than one case. Worth following even when the co-accused names don't match." },
                { icon: '🟠', term: 'Thick orange edge',      desc: 'Same species and same location. The strongest link the graph can show — unlikely to be a coincidence.' },
                { icon: '🔗', term: 'Bridge node',            desc: "Sits between two clusters that otherwise don't touch. Often the one actually connecting two networks." },
                { icon: '⚫', term: 'Isolated node',          desc: 'No links under the current filters. Could be a lone operator, or the data on them is just thin.' },
              ].map((r) => (
                <div key={r.term} className="flex gap-3 px-3 py-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <span className="text-base shrink-0 mt-0.5">{r.icon}</span>
                  <div>
                    <p className="text-white/80 text-xs font-semibold">{r.term}</p>
                    <p className="text-white/40 text-[11px] mt-0.5">{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Controls */}
          <Section title="The controls">

            <ControlBlock
              icon="🐘"
              label="Species mode"
              color="text-green-400"
              borderColor="border-green-500/25"
              bg="rgba(74,222,128,0.06)"
            >
              Only connects two offenders if they were caught with the same species. Good for questions like
              &quot;who else is in the ivory trade&quot; or &quot;is rosewood its own network.&quot;
            </ControlBlock>

            <ControlBlock
              icon="📍"
              label="Location mode"
              color="text-sky-400"
              borderColor="border-sky-500/25"
              bg="rgba(96,165,250,0.06)"
            >
              Only connects offenders arrested in the same place. Good for spotting a port, a border crossing, or a
              town that keeps coming up in cases.
            </ControlBlock>

            <ControlBlock
              icon="⚡"
              label="Both mode"
              color="text-orange-400"
              borderColor="border-orange-500/25"
              bg="rgba(249,115,22,0.06)"
            >
              Connects on either species or location, and turns the line orange when it&apos;s both. If you only look at
              one thing in this whole view, look at the orange lines.
            </ControlBlock>

            <div className="mt-3 rounded-xl border border-white/8 px-4 py-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <p className="text-white/65 text-xs font-semibold mb-1">Min Links (1–5)</p>
              <p className="text-white/40 text-[11px] leading-relaxed">
                At 1, any shared attribute counts, so you&apos;ll see everything — but it&apos;s noisy.
                <br /><br />
                At 2–3, one-off coincidences drop out and the real relationships start to stand out on their own.
                <br /><br />
                At 4–5, only the strongest, most repeated links survive. If two people are still connected at 4,
                that&apos;s not a coincidence worth dismissing.
              </p>
              <p className="text-white/30 text-[10px] mt-2 italic">
                Start at 1 to see the whole picture, then move to 3 to cut the noise.
              </p>
            </div>
          </Section>

          {/* Interaction */}
          <Section title="Using the graph">
            <div className="flex flex-col gap-1.5">
              {[
                ['Hover a node',          'Quick info — name, nationality, case count. No click needed.'],
                ['Click a node',          'Opens the full profile on the right: case count, species, locations, everyone they’re linked to.'],
                ['Drag a node',           'Move it by hand if the layout looks cramped. It settles again once you let go.'],
                ['↺ Re-layout',           'Reruns the layout from scratch.'],
                ['Search box',            'Fades out everyone except matches for the name or nationality you type.'],
              ].map(([action, desc]) => (
                <div key={action} className="flex gap-3 items-start px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.025)' }}>
                  <span className="text-green-400 text-[10px] font-bold shrink-0 mt-0.5 w-28">{action}</span>
                  <span className="text-white/40 text-[11px]">{desc}</span>
                </div>
              ))}
            </div>
          </Section>

          {/* Scope filters */}
          <Section title="Filters">
            <p>
              The graph only draws the top {MAX_NODES} offenders by case count, to keep the physics readable. That can
              leave out exactly the person worth finding — a broker with two or three cases won&apos;t have a high case
              count, even if they&apos;re the one actually linking two networks.
            </p>
            <p className="mt-2">
              That&apos;s what the nationality, species, status and date filters above the graph are for. They narrow the
              data down first, before the top-{MAX_NODES} cutoff applies. Filter to one species or one country and
              anyone in that scope comes back into view — the numbers and the graph both follow whatever you&apos;ve filtered to.
            </p>
          </Section>

          {/* Path finder */}
          <Section title="Trace Path">
            <p>
              Sometimes &quot;who&apos;s directly connected&quot; isn&apos;t the question — you want to know if two specific people are
              connected at all, even through a few steps in between.
            </p>
            <p className="mt-2">
              Turn on Trace Path, click two offenders, and the shortest chain between them lights up in yellow. If
              nothing lights up, that doesn&apos;t prove there&apos;s no relationship — try lowering Min Links or switching to
              Both mode first.
            </p>
          </Section>

          {/* Aliases */}
          <Section title="Aliases">
            <p>
              Some records list a name like &quot;Jeremiah Kodi alias Rasta.&quot; If a different case only uses one of those
              two names, that&apos;s the same person showing up as two strangers.
            </p>
            <p className="mt-2">
              We catch that pattern now and merge both spellings into one node. If a node has a known alias, you&apos;ll
              see &quot;Also known as&quot; in its profile — masked the same way names normally are for your role.
            </p>
          </Section>

          {/* Export */}
          <Section title="Export">
            <p>
              PNG saves a snapshot of whatever&apos;s on screen right now — useful for a case file or a briefing.
            </p>
            <p className="mt-2">
              CSV exports every offender currently in the graph (case count, species, locations, aliases) and every
              link between them. Both only export what&apos;s currently in scope.
            </p>
          </Section>

          {/* Cluster count */}
          <Section title='What "Clusters" means'>
            <p>
              It&apos;s a count of how many separate groups exist in the graph right now. 1 means everyone&apos;s reachable from
              everyone else somehow — one big network. A high number means lots of small groups with no links between them.
            </p>
            <p className="mt-2 text-white/40 text-[11px]">
              Turning up Min Links usually raises this number, since the weaker links break first. Watch which groups
              split off when you do — each one is its own thread worth following.
            </p>
          </Section>

        </div>
      </Drawer>
    </div>
  );
}

/* ── Small layout helpers ──────────────────────────────────────────── */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <p className="text-white/85 text-xs font-bold uppercase tracking-[0.1em]">{title}</p>
        <div className="h-px flex-1 bg-white/6" />
      </div>
      <div className="text-white/55 text-[12px] leading-relaxed">{children}</div>
    </div>
  );
}

function ControlBlock({
  icon, label, color, borderColor, bg, children,
}: {
  icon: string; label: string; color: string; borderColor: string; bg: string; children: React.ReactNode;
}) {
  return (
    <div className={`rounded-xl border ${borderColor} px-4 py-3 mb-2`} style={{ background: bg }}>
      <p className={`text-xs font-semibold mb-1 ${color}`}>{icon} {label}</p>
      <p className="text-white/45 text-[11px] leading-relaxed">{children}</p>
    </div>
  );
}
